import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Loader2 } from 'lucide-react';
import { useKnowledgeBase, useUpdateKnowledgeBase } from '../../hooks/use-knowledge-base';
import { KnowledgeBaseModalProps, KnowledgeBaseFormData } from '../../types/knowledge-base';
import { useToast } from 'hooks/use-toast';

/**
 * Custom Knowledge Base Modal Component
 * 
 * A modal component that allows users to edit their personalization settings
 * for the AI assistant including nickname, profession, personality traits, and additional context.
 */
export const KnowledgeBaseModal: React.FC<KnowledgeBaseModalProps> = ({
    isOpen,
    onClose,
    onSave
}) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    // Knowledge base data and mutations
    const { data: knowledgeBaseData, isLoading: isLoadingData } = useKnowledgeBase();
    const updateKnowledgeBase = useUpdateKnowledgeBase();



    // Form state
    const [formData, setFormData] = useState<KnowledgeBaseFormData>({
        user_nickname: '',
        user_profession: '',
        llm_personality: '',
        description: '',
        is_enabled: true
    });

    const [hasChanges, setHasChanges] = useState(false);
    const [enableForNewChat, setEnableForNewChat] = useState(true);

    // Initialize form data when knowledge base data is loaded
    useEffect(() => {
        if (knowledgeBaseData) {
            setFormData(knowledgeBaseData);
            setEnableForNewChat(knowledgeBaseData.is_enabled);
        } else {
            // Reset to empty form if no data
            setFormData({
                user_nickname: '',
                user_profession: '',
                llm_personality: '',
                description: '',
                is_enabled: true
            });
        }
        setHasChanges(false);
    }, [knowledgeBaseData]);

    // Handle form field changes
    const handleInputChange = (field: keyof KnowledgeBaseFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setHasChanges(true);
    };

    // Handle form submission
    const handleSave = async () => {
        try {
            const result = await updateKnowledgeBase.mutateAsync(formData);

            if (result) {
                toast({
                    variant: 'success',
                    title: t('SUCCESS'),
                    description: t('KNOWLEDGE_BASE_UPDATE_SUCCESS')
                });
                setHasChanges(false);
                onSave?.(formData);
                onClose();
            } else {
                toast({
                    variant: 'destructive',
                    title: t('SUCCESS'),
                    description: t('KNOWLEDGE_BASE_UPDATE_ERROR')
                });
            }
        } catch (error) {
            console.error('Failed to update knowledge base:', error);
            toast({
                variant: 'destructive',
                title: t('SUCCESS'),
                description: t('KNOWLEDGE_BASE_UPDATE_ERROR')
            });
        }
    };

    // Handle modal close
    const handleClose = () => {
        if (hasChanges) {
            // Could add a confirmation dialog here
            // For now, just close
        }
        // reset form data when closing to avoid stale data
        if (knowledgeBaseData) {
            setFormData(knowledgeBaseData);
            setEnableForNewChat(knowledgeBaseData.is_enabled);
        } else {
            // Reset to empty form if no data
            setFormData({
                user_nickname: '',
                user_profession: '',
                llm_personality: '',
                description: '',
                is_enabled: true
            });
        }
        setHasChanges(false);
        onClose();
    };

    // Reset form when modal opens or when data changes
    useEffect(() => {
        if (isOpen) {
            if (knowledgeBaseData) {
                setFormData(knowledgeBaseData);
            } else {
                // Reset to empty form if no data
                setFormData({
                    user_nickname: '',
                    user_profession: '',
                    llm_personality: '',
                    description: '',
                    is_enabled: true
                });
            }
            setHasChanges(false);
        }
    }, [isOpen, knowledgeBaseData]);

    const isLoading = updateKnowledgeBase.isPending;
    const isSaveDisabled = !hasChanges || isLoading;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader className="pb-6">
                    <DialogTitle className="text-xl ">
                        {t('CUSTOM_KNOWLEDGE_BASE') || 'Custom Knowledge Base'}
                    </DialogTitle>
                    <DialogDescription className=" text-sm">
                        {t('KNOWLEDGE_BASE_DESCRIPTION') || 'Add personalized responses.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 space-y-6">
                    {isLoadingData ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin" />
                            <span className="ml-2">{t('LOADING')}</span>
                        </div>
                    ) : (
                        <>
                            {/* User Nickname Field */}
                            <div className="space-y-2">
                                <Label htmlFor="userNickname" className="text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_NICKNAME_LABEL') || 'What should SeliseAI call you?'}
                                </Label>
                                <Input
                                    id="userNickname"
                                    disabled={!enableForNewChat}
                                    value={formData.user_nickname}
                                    onChange={(e) => handleInputChange('user_nickname', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Luca Meier'}
                                    maxLength={50}
                                    className="h-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>

                            {/* User Profession Field */}
                            <div className="space-y-2">
                                <Label htmlFor="userProfession" className="text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_PROFESSION_LABEL') || 'What is your profession?'}
                                </Label>
                                <Input
                                    disabled={!enableForNewChat}
                                    id="userProfession"
                                    value={formData.user_profession}
                                    onChange={(e) => handleInputChange('user_profession', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Write here'}
                                    maxLength={100}
                                    className="h-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>

                            {/* LLM Personality Field */}
                            <div className="space-y-2">
                                <Label htmlFor="llmPersonality" className="text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_PERSONALITY_LABEL') || 'What trait should SeliseAI have?'}
                                </Label>
                                <Textarea
                                    id="llmPersonality"
                                    disabled={!enableForNewChat}
                                    value={formData.llm_personality}
                                    onChange={(e) => handleInputChange('llm_personality', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Write here'}
                                    rows={3}
                                    maxLength={300}
                                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            {/* Description Field */}
                            <div className="space-y-2">
                                <Label htmlFor="description" className="text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_DESCRIPTION_LABEL') || 'Anything else SeliseAI should know about you?'}
                                </Label>
                                <Textarea
                                    id="description"
                                    disabled={!enableForNewChat}
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Write here'}
                                    rows={4}
                                    maxLength={500}
                                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            {/* Enable for new chat toggle */}

                        </>
                    )}
                </div>

                <div className="flex flex-row justify-between">
                    <div className="flex flex-row items-center justify-start pt-4 gap-2">
                        <Switch
                            id="enableForNewChat"
                            checked={enableForNewChat}
                            onCheckedChange={(checked) => {
                                setFormData(prev => ({
                                    ...prev,
                                    is_enabled: checked
                                }));
                                setEnableForNewChat(checked);
                                setHasChanges(true);
                            }}
                        />
                        <Label htmlFor="enableForNewChat" className="text-sm font-medium">
                            {t('KNOWLEDGE_BASE_ENABLE_FOR_NEW_CHAT') || 'Enable for new chat'}
                        </Label>

                    </div>
                    <div className="flex flex-row justify-end pt-6 gap-4">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            className="w-24 h-10"
                            disabled={isLoading}
                        >
                            {t('CANCEL') || 'Cancel'}
                        </Button>
                        <Button
                            onClick={handleSave}
                            className="w-24 h-10"
                            disabled={isSaveDisabled}
                        >
                            {isLoading ? (
                                <>
                                    {/*<Loader2 className="h-4 w-4 animate-spin mr-1" />*/}
                                    {t('SAVING') || 'Saving...'}
                                </>
                            ) : (
                                t('SAVE') || 'Save'
                            )}
                        </Button>
                    </div>

                </div>
            </DialogContent>
        </Dialog>
    );
};
